import React from 'react';
import ReactDOM from 'react-dom';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';

/** Portal so the modal sits above all stacking contexts */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const TimePickerField = ({
  label,
  timeValue,
  value,
  required = false, // builder-time default
  left = 0,
  top = 0,
  width = 200,
  height = 40,
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));
  const interactionMode = useInteractionMode();
  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const { selected } = useSelector((s: any) => s.formToggle);

  const [isDragging, setDragging] = React.useState(false);
  const [showEditor, setShowEditor] = React.useState(false);

  // Close modal on Escape
  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  // -------- interactions --------
  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onDoubleClick = () => {
    if (!enabled) return;
    if (selected === 'FORM') setShowEditor(true); // editor only in FORM
  };

  const handleDelete = () => {
    if (selected === 'FORM') actions.delete(id);
  };

  // -------- classes per view --------
  function cls() {
    if (selected === 'REPORT') {
      return {
        wrapper: styles.wrapperReport,
        label: styles.labelReport,
        inputContainer: styles.inputContainerReport,
        timeInput: styles.timeInputReport,
        row: styles.rowReport,
        value: styles.valueReport,
      };
    }
    return {
      wrapper: styles.wrapperForm,
      label: styles.labelForm,
      inputContainer: styles.inputContainerForm,
      timeInput: styles.timeInputForm,
      row: styles.rowReport, // not used in FORM
      value: styles.valueReport, // not used in FORM
    };
  }

  // -------- format HH:MM for builder preview --------
  const parts = (timeValue || '').split(':');
  const hRaw = parts[0] ?? '';
  const mRaw = parts[1] ?? '';
  const hh = hRaw ? String(hRaw).padStart(2, '0') : 'HH';
  const mm = mRaw ? String(mRaw).padStart(2, '0') : 'MM';

  const handleChange = (val: string | null) => {
    if (!enabled) {
      setProp((props: any) => {
        props.timeValue = val || '';
      });
    }
  };

  const isView = interactionMode === 'VIEW';
  const isFill = interactionMode === 'FILL';
  const isForm = selected === 'FORM';

  return (
    <>
      <div
        ref={(ref) => ref && connect(ref)}
        className={cls().wrapper}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        role={enabled ? 'button' : undefined}
        tabIndex={enabled ? 0 : undefined}
        data-required={String(!!required)}
      >
        {isView && (
          <div className={styles.viewTimeContainer}>
            <p className={styles.viewTimeLabel}>
              {label}
              {required && (
                <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                  *
                </span>
              )}
            </p>
            <p className={styles.viewTimeValue}>{`${value || ''}`}</p>
          </div>
        )}

        {!isView && (
          <>
            <div className={cls().label}>
              {label}
              {required && (
                <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                  *
                </span>
              )}
              {enabled && isForm && (
                <div className={styles.deleteButton} onClick={handleDelete}>
                  ×
                </div>
              )}
            </div>

            <div className={cls().inputContainer} style={{ width, height }}>
              {enabled ? (
                <div className={styles.fakeTimeDisplay}>
                  <div className={styles.timeBox}>{hh}</div>
                  <div className={styles.colon}>:</div>
                  <div className={styles.timeBox}>{mm}</div>
                </div>
              ) : (
                <TimePicker
                  value={timeValue || ''}
                  onChange={handleChange}
                  className={cls().timeInput}
                  disableClock
                  clearIcon={null}
                  clockIcon={null}
                  required={isFill ? !!required : false}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* === Centered modal editor (FORM only) === */}
      {showEditor && isForm && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() => setShowEditor(false)} /* click backdrop closes */
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit time field"
              onMouseDown={(e) => e.stopPropagation()} /* keep clicks inside */
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Time Field</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.label = e.target.value;
                      })
                    }
                  />
                </div>

                {/* New: builder-time required toggle */}
                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`required-${id}`}
                    type="checkbox"
                    checked={!!required}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.required = e.target.checked;
                      })
                    }
                  />
                  <label style={{ marginLeft: 6 }} htmlFor={`required-${id}`}>
                    Required field
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowEditor(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(TimePickerField as any).craft = {
  displayName: 'TimePickerField',
  props: {
    label: 'Select time',
    timeValue: '',
    value: '',
    required: false, // default off
    left: 0,
    top: 0,
    width: 200,
    height: 40,
  },
};

export default TimePickerField;
